import { cn } from "@/lib/utils";

/** UI type roles — Helvetica product typography shared with the homepage. */
const typeRoleStyles = {
	appLabel: "text-xxs font-medium uppercase leading-none ",
	navLabel: "text-sm font-medium leading-normal tracking-normal",
	sectionLabel: "text-xxs font-medium uppercase leading-none ",
	meta: "text-xs font-normal leading-[1.4]",
	postMeta: "text-sm font-medium leading-normal tracking-[0.01em]",
	postTitle: "text-card-title font-medium leading-[1.2] tracking-[-0.01em]",
	postBody: "text-base font-normal leading-[1.55] tracking-normal",
	bodyDense: "text-base font-normal leading-[1.55] tracking-normal",
	bodyLarge: "text-md font-normal leading-[1.55] tracking-normal",
	actionLabel: "text-xxs font-medium uppercase leading-none tracking-[0.06em]",
	cardTitle: "text-card-title font-medium leading-[1.2] tracking-[-0.01em]",
	pageTitle:
		"text-page-title font-normal leading-[1.1] tracking-[-0.025em] text-balance",
	displayTitle:
		"text-page-title font-normal leading-[1.05] tracking-[-0.025em] text-balance",
} as const;

type TypeRole = keyof typeof typeRoleStyles;

const sizeClasses = {
	"2xs": "text-[0.625rem]",
	xxs: "text-[0.825rem]",
	xs: "text-[11px] md:text-xs",
	sm: "text-[13px] md:text-sm",
	base: "text-[15px] md:text-base",
	md: "text-[15px] md:text-base",
	lg: "text-base md:text-lg",
	xl: "text-[15px] md:text-xl",
	"2xl": "text-lg md:text-2xl",
	"3xl": "text-xl md:text-3xl",
	"4xl": "text-2xl md:text-4xl",
} as const;

const weightClasses = {
	300: "font-light",
	400: "font-normal",
	500: "font-medium",
	600: "font-semibold",
	700: "font-bold",
	800: "font-extrabold",
} as const;

const emphasisToWeight = {
	light: "font-light",
	normal: "font-normal",
	medium: "font-medium",
	semibold: "font-semibold",
	bold: "font-bold",
	extraBold: "font-extrabold",
} as const;

const colorClasses = {
	primary: "text-content-primary",
	secondary: "text-content-secondary",
	tertiary: "text-content-tertiary",
	quaternary: "text-content-quaternary",
	inverse: "text-content-inverse",
	muted: "text-muted-foreground",
	brand: "text-brand",
	danger: "text-danger",
	positive: "text-positive",
	warning: "text-warning",
	info: "text-info",
} as const;

const leadingClasses = {
	tight: "leading-tight",
	snug: "leading-snug",
	normal: "leading-normal",
	relaxed: "leading-relaxed",
} as const;

type TextSize = keyof typeof sizeClasses;
type TextWeight = keyof typeof weightClasses;
type TextEmphasis = keyof typeof emphasisToWeight;
type TextColor = keyof typeof colorClasses;
type TextLeading = keyof typeof leadingClasses;

interface TextOwnProps {
	children: React.ReactNode;
	className?: string;
	size?: TextSize;
	weight?: TextWeight;
	emphasis?: TextEmphasis;
	color?: TextColor;
	leading?: TextLeading;
	role?: TypeRole;
	as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
	uppercase?: boolean;
}

type TextProps = TextOwnProps &
	Omit<React.HTMLAttributes<HTMLElement>, keyof TextOwnProps>;

export function Text({
	children,
	className,
	size,
	weight,
	emphasis,
	color,
	leading,
	role,
	as = "p",
	uppercase = false,
	...rest
}: TextProps) {
	const Component = as;

	return (
		<Component
			className={cn(
				"font-sans",
				!role && !size && "text-base leading-[1.55] tracking-[-0.005em]",
				role && typeRoleStyles[role],
				size && sizeClasses[size],
				weight && weightClasses[weight],
				!weight && emphasis && emphasisToWeight[emphasis],
				color && colorClasses[color],
				leading && leadingClasses[leading],
				uppercase && "uppercase",
				className,
			)}
			{...rest}
		>
			{children}
		</Component>
	);
}

export { typeRoleStyles };
export type { TextProps, TypeRole, TextSize, TextColor };
